import React, { useState, useEffect } from 'react'
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import { APP_VERSION } from '@/version'
import toast from 'react-hot-toast'
import { 
  Pizza, Coffee, Utensils, Star, ThumbsUp, UserX, 
  Plus, ChefHat, Clock, Users, TrendingUp, Package,
  ChevronLeft, ChevronRight
} from 'lucide-react'
import McDonaldsOrder from './McDonaldsOrder'

interface MealSlot {
  id: string
  date: string
  meal_type: 'breakfast' | 'lunch' | 'dinner'
  slot_type?: 'breakfast' | 'lunch' | 'dinner' // For backwards compatibility
  meal_id?: string
  meal_name?: string
  meal_category?: string
  category?: string
  who_cooks?: string
  cook_name?: string
  cook_id?: string
  notes?: string
  description?: string
  emoji?: string
  user_opted_out?: boolean
  opt_out_count?: number
  top_suggestions?: Array<{
    id: string
    meal_name: string
    avg_rating: number
    vote_count: number
  }>
}

interface MealSuggestion {
  id: string
  meal_date: string
  meal_type: string
  meal_name: string
  notes?: string
  suggested_by: string
  total_votes: number
  avg_rating: number
  user_voted: boolean
  user_vote?: number
}

const mealTypeIcons = {
  breakfast: <Coffee className="w-5 h-5" />,
  lunch: <Pizza className="w-5 h-5" />,
  dinner: <Utensils className="w-5 h-5" />
}

const mealTypeColors = {
  breakfast: 'bg-yellow-100 text-yellow-800',
  lunch: 'bg-blue-100 text-blue-800',
  dinner: 'bg-purple-100 text-purple-800'
}

// meal_enabled_types is stored as a JSON array string; older rows may be a
// comma-separated list.
const parseMealTypes = (raw: unknown): string[] | null => {
  if (Array.isArray(raw)) return raw.filter((t): t is string => typeof t === 'string')
  if (typeof raw !== 'string' || !raw.trim()) return null
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed.filter((t): t is string => typeof t === 'string')
  } catch {
    // fall through to comma-separated
  }
  const list = raw.split(',').map(t => t.trim()).filter(Boolean)
  return list.length ? list : null
}

export function KidMealPlanner() {
  const { user } = useAuth()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [mealSlots, setMealSlots] = useState<MealSlot[]>([])
  const [weekMeals, setWeekMeals] = useState<Record<string, MealSlot[]>>({}) // Store meals for the whole week
  const [suggestions, setSuggestions] = useState<MealSuggestion[]>([])
  const [popularMeals, setPopularMeals] = useState<any[]>([])
  const [showSuggestModal, setShowSuggestModal] = useState(false)
  const [selectedSlotType, setSelectedSlotType] = useState<string>('')
  const [newSuggestion, setNewSuggestion] = useState({
    meal_name: '',
    notes: ''
  })
  const [loading, setLoading] = useState(false)
  const [showMcDonaldsOrder, setShowMcDonaldsOrder] = useState(false)
  const [mcDonaldsSlotId, setMcDonaldsSlotId] = useState<string | null>(null)
  const [enabledMealTypes, setEnabledMealTypes] = useState<string[]>(['dinner']) // Default to dinner
  const [mealConfig, setMealConfig] = useState<any>({})

  useEffect(() => {
    loadMealConfig()
  }, [])

  useEffect(() => {
    loadMealData()
    loadWeekMeals()
    loadPopularMeals()
  }, [selectedDate])

  const loadMealConfig = async () => {
    try {
      // Raw system_settings rows (meal_* / dinner_*), values as strings.
      const config = await apiClient.get('/api/kids/meal-config')
      const types = parseMealTypes(config?.meal_enabled_types ?? config?.enabled_meal_types)
      if (types) {
        setEnabledMealTypes(types)
      }
      if (config) {
        setMealConfig(config)
      }
    } catch (error) {
      console.error('Failed to load meal config, using defaults:', error)
      // Keep default values
    }
  }

  const loadMealData = async () => {
    setLoading(true)
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd')

      // Load meal slots - backend now ensures they exist
      const slots = await apiClient.get(`/api/kids/meal-slots/${dateStr}`)
      console.log('Loaded meal slots:', slots)

      // Set slots directly - backend returns them in the right format
      setMealSlots(slots || [])

      // Load suggestions
      const suggResponse = await apiClient.get(`/api/kids/meal-suggestions/${dateStr}`).catch(() => ({ suggestions: [] }))
      setSuggestions(suggResponse?.suggestions || [])
    } catch (error) {
      console.error('Failed to load meal data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadWeekMeals = async () => {
    try {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 })
      const weekMealData: Record<string, MealSlot[]> = {}

      // Load meals for each day of the week
      for (let i = 0; i < 7; i++) {
        const day = addDays(weekStart, i)
        const dateStr = format(day, 'yyyy-MM-dd')
        try {
          const slots = await apiClient.get(`/api/kids/meal-slots/${dateStr}`)
          if (slots && slots.length > 0) {
            weekMealData[dateStr] = slots
          }
        } catch (error) {
          // Silently ignore errors for individual days
        }
      }

      setWeekMeals(weekMealData)
    } catch (error) {
      console.error('Failed to load week meals:', error)
    }
  }

  const loadPopularMeals = async () => {
    try {
      const meals = await apiClient.get('/api/kids/popular-meals')
      setPopularMeals(meals || [])
    } catch (error) {
      console.error('Failed to load popular meals:', error)
    }
  }

  const handleSuggestMeal = async () => {
    if (!newSuggestion.meal_name) {
      toast.error('Please enter a meal name')
      return
    }

    try {
      await apiClient.post('/api/kids/vote-meal', {
        meal_id: '1', // TODO: Need meal ID from selection
        meal_type: selectedSlotType,
        date: format(selectedDate, 'yyyy-MM-dd')
      })
      
      toast.success('Meal suggestion added!')
      setShowSuggestModal(false)
      setNewSuggestion({ meal_name: '', notes: '' })
      loadMealData()
    } catch (error) {
      toast.error('Failed to suggest meal')
    }
  }

  const handleVote = async (suggestionId: string, vote: number) => {
    try {
      await apiClient.post('/api/meal-voting/vote', {
        suggestion_id: suggestionId,
        vote
      })
      
      toast.success(`Voted ${vote} stars!`)
      loadMealData()
    } catch (error) {
      toast.error('Failed to vote')
    }
  }

  const handleOptOut = async (slotId: string, optIn: boolean = false) => {
    try {
      if (optIn) {
        await apiClient.delete(`/api/meal-voting/opt-out/${slotId}`)
        toast.success("You're back in for this meal!")
      } else {
        await apiClient.post('/api/meal-voting/opt-out', {
          meal_slot_id: slotId,
          reason: "Making my own meal"
        })
        toast.success("You are opted out of this meal.")
      }
      loadMealData()
    } catch (error) {
      toast.error('Failed to update meal preference')
    }
  }

  const renderStars = (rating: number, onVote?: (vote: number) => void) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onClick={() => onVote?.(star)}
            className={`${onVote ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
            disabled={!onVote}
          >
            <Star 
              className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
      </div>
    )
  }

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 })
    return addDays(weekStart, i)
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <ChefHat className="w-6 h-6 mr-2 text-orange-500" />
          Meal Planning & Voting
        </h2>

        {/* Week Navigation */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setSelectedDate(prev => addDays(prev, -7))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Previous Week"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={() => setSelectedDate(new Date())}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Today
          </button>
          
          <button
            onClick={() => setSelectedDate(prev => addDays(prev, 7))}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Next Week"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          <div className="ml-4 text-sm text-gray-600">
            Week of {format(startOfWeek(selectedDate, { weekStartsOn: 0 }), 'MMM d, yyyy')}
          </div>
        </div>
        
        <div className="flex gap-2 overflow-x-auto pb-2">
          {weekDays.map(day => {
            const dayStr = format(day, 'yyyy-MM-dd')
            const dayMeals = weekMeals[dayStr] || []
            const dinnerMeal = dayMeals.find(m => m.slot_type === 'dinner' || m.meal_type === 'dinner')
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => setSelectedDate(day)}
                className={`px-3 py-2 rounded-lg whitespace-nowrap transition-colors min-w-[100px] ${
                  format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <div className="text-xs">{format(day, 'EEE')}</div>
                <div className="font-semibold">{format(day, 'MMM d')}</div>
                {dinnerMeal?.meal_name && (
                  <div className="text-xs mt-1 truncate" title={dinnerMeal.meal_name}>
                    {dinnerMeal.emoji || '🍽️'} {dinnerMeal.meal_name}
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Meal Slots for Selected Day */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <h3 className="font-semibold mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          {format(selectedDate, 'EEEE, MMMM d')} Meals
        </h3>

        <div className="space-y-4">
          {enabledMealTypes.map(mealType => {
            const slot = mealSlots.find(s => (s.slot_type === mealType || s.meal_type === mealType))
            const mealSuggestions = Array.isArray(suggestions) ? suggestions.filter(s => s?.meal_type === mealType) : []
            
            return (
              <div key={mealType} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {mealTypeIcons[mealType as keyof typeof mealTypeIcons]}
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      mealTypeColors[mealType as keyof typeof mealTypeColors]
                    }`}>
                      {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedSlotType(mealType)
                      setShowSuggestModal(true)
                    }}
                    className="text-blue-500 hover:bg-blue-50 p-1 rounded"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>

                {/* Current Meal */}
                {slot ? (
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {slot.emoji && <span className="text-xl">{slot.emoji}</span>}
                          {slot.meal_name || `Today's ${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`}
                        </div>
                        {slot.cook_name && (
                          <div className="text-sm text-gray-600 mt-1">
                            Cook: {slot.cook_name}
                          </div>
                        )}
                      </div>
                      {/* McDonald's Order Button */}
                      {slot.meal_name && slot.meal_name.toLowerCase().includes('mcdonald') && (
                        <button
                          onClick={() => {
                            setMcDonaldsSlotId(slot.id)
                            setShowMcDonaldsOrder(true)
                          }}
                          className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
                        >
                          <Package className="w-4 h-4" />
                          <span>My Order</span>
                        </button>
                      )}
                    </div>
                    {slot.notes && (
                      <div className="text-sm text-gray-500 mt-1">{slot.notes}</div>
                    )}
                    
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-2 text-sm">
                        {slot.opt_out_count > 0 && (
                          <span className="text-orange-600">
                            <UserX className="w-4 h-4 inline mr-1" />
                            {slot.opt_out_count} opting out
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={() => handleOptOut(slot.id, slot.user_opted_out)}
                        className={`px-3 py-1 rounded text-sm transition-colors ${
                          slot.user_opted_out
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        title={slot.user_opted_out ? "Click to join the family dinner" : "I'll make my own meal tonight"}
                      >
                        {slot.user_opted_out ? "Join Dinner" : "Make My Own (not eating planned meal tonight)"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 rounded-lg p-3 mb-3 text-center text-gray-600">
                    No meal planned yet - suggest one!
                  </div>
                )}

                {/* Suggestions */}
                {mealSuggestions.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700">Suggestions:</div>
                    {mealSuggestions.map(suggestion => (
                      <div key={suggestion.id} className="bg-blue-50 rounded-lg p-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm">{suggestion.meal_name}</div>
                            <div className="text-xs text-gray-600">
                              by {suggestion.suggested_by}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {renderStars(
                              suggestion.user_vote || 0,
                              (vote) => handleVote(suggestion.id, vote)
                            )}
                            <div className="text-xs text-gray-500">
                              {(suggestion.avg_rating || suggestion.rating || 4.0).toFixed(1)} ({suggestion.total_votes || 0} votes)
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Top suggestions from slot data */}
                {slot?.top_suggestions && slot.top_suggestions.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    <TrendingUp className="w-3 h-3 inline mr-1" />
                    Top picks: {slot.top_suggestions.map(s => s.meal_name).join(', ')}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Popular Meals */}
      {popularMeals.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="font-semibold mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-green-500" />
            Family Favorites
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {popularMeals.slice(0, 6).map((meal, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-sm">{meal.meal_name}</div>
                  <div className="text-xs text-gray-600">
                    {meal.meal_type} • {meal.total_votes} votes
                  </div>
                </div>
                <div className="text-right">
                  {renderStars(
                    meal.user_vote || 0,
                    async (rating) => {
                      try {
                        await apiClient.post('/api/kids/vote-meal', {
                          meal_id: meal.id,
                          rating
                        })
                        toast.success(`Rated ${rating} stars!`)
                        loadPopularMeals()
                      } catch (error) {
                        toast.error('Failed to vote')
                      }
                    }
                  )}
                  <div className="text-xs text-gray-500">
                    {meal.avg_rating > 0 ? meal.avg_rating.toFixed(1) : '—'} avg
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggest Modal */}
      {showSuggestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-4">
              Suggest {selectedSlotType} for {format(selectedDate, 'MMM d')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Meal Name</label>
                <input
                  type="text"
                  value={newSuggestion.meal_name}
                  onChange={(e) => setNewSuggestion({...newSuggestion, meal_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Spaghetti and Meatballs"
                  autoFocus
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  value={newSuggestion.notes}
                  onChange={(e) => setNewSuggestion({...newSuggestion, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                  placeholder="Any special requests or notes"
                />
              </div>

              {/* Quick suggestions from popular meals */}
              {popularMeals.filter(m => m.meal_type === selectedSlotType).length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-2">Popular choices:</div>
                  <div className="flex flex-wrap gap-2">
                    {popularMeals
                      .filter(m => m.meal_type === selectedSlotType)
                      .slice(0, 3)
                      .map((meal, idx) => (
                        <button
                          key={idx}
                          onClick={() => setNewSuggestion({...newSuggestion, meal_name: meal.meal_name})}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                        >
                          {meal.meal_name}
                        </button>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleSuggestMeal}
                className="flex-1 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
              >
                Suggest Meal
              </button>
              <button
                onClick={() => {
                  setShowSuggestModal(false)
                  setNewSuggestion({ meal_name: '', notes: '' })
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* McDonald's Order Modal */}
      {showMcDonaldsOrder && mcDonaldsSlotId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <McDonaldsOrder 
              slotId={mcDonaldsSlotId} 
              onClose={() => {
                setShowMcDonaldsOrder(false)
                setMcDonaldsSlotId(null)
                loadMealData() // Reload to show updated orders
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}